import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { from } from 'rxjs';
import { CommonConstant } from 'src/shared/constants/common.constants';
import { FirebaseService } from 'src/shared/firebase/services/firebase.service';
import { LocalSpinnerService } from 'src/shared/modules/local-spinner/local-spinner.service';
import { environment } from '../../../environments/environment';
import { BaseComponent } from '../base-component/base.component';

export interface FavieFileTemp {
  file?: File;
  src?: any;
  event?: any;
  url?: string;
}

@Component({
  selector: 'favie-upload-file',
  templateUrl: './favie-upload-file.component.html',
  styleUrls: ['./favie-upload-file.component.scss'],
})
export class FavieUploadFileComponent extends BaseComponent {
  public readonly UploadingSpinner = 'UploadFileComponent_UploadingSpinner';

  private readonly DEFAULT_SIZE_LIMIT = environment.fileUploadMaximumFileSize
    ? environment.fileUploadMaximumFileSize / 1024
    : 1024 * 1024;

  public UploadingSpinnerId: string;

  @Input() label: string;
  @Input() iconUrl: string;
  @Input() limitSize = this.DEFAULT_SIZE_LIMIT;
  @Input() file: FavieFileTemp;
  @Input() path: string;
  @Input() isModifiedFromReviewEdition = false;
  @Input() isDisabled: boolean;
  @Input() prefixFileName: string;
  @Input() uploadedUrl: string;

  @Output() fileChange: EventEmitter<FavieFileTemp> = new EventEmitter<FavieFileTemp>();
  @Output() emitErrors: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('fileInputElement') fileInputElement: ElementRef;

  constructor(
    private readonly matSnackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly localSpinnerService: LocalSpinnerService,
    private readonly firebaseService: FirebaseService,
  ) {
    super();
    this.UploadingSpinnerId = this.UploadingSpinner + Math.random();
  }

  protected onInit() {
    console.log(this.uploadedUrl, this.prefixFileName);
    if (this.uploadedUrl) {
      this._initFile(this.uploadedUrl, this.prefixFileName || '%2F');
    }
  }

  get canUpload() {
    return !this.file;
  }

  public triggerUpload() {
    this.fileInputElement.nativeElement.click();
  }

  public remove() {
    this.notifyChange(undefined);
  }

  public onUploadedFile($event) {
    const files: File[] = Array.from($event.target.files || []);
    if (files && files.length < 1) {
      return;
    }

    const fileReadingProcesses$ = from(this.readFileToDataUrl(files[0]));
    this.addSubscription(this.localSpinnerService.withLocalSpinner(fileReadingProcesses$, this.UploadingSpinnerId).subscribe(
      file => this.notifyChange(file),
      error => this.matSnackBar.open(error, null, CommonConstant.FAILURE_SNACKBAR_CONFIG)
    ));
  }

  private readFileToDataUrl(file: File): Promise<FavieFileTemp> {
    return new Promise((resolve, reject) => {
      if (file.size > 1024 * this.limitSize) {
        this.emitErrors.emit('fileSizeLimitExceeded');
        reject(this.translateService.instant('Global.UploadPicture.Error.Size', { size: this.limitSize }));
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({ file, src: e.target.result } as FavieFileTemp);
      };
      reader.readAsDataURL(file);
    });
  }

  private notifyChange(file: FavieFileTemp) {
    this.file = file;
    if (this.file?.file) {
      this.subscribe(this.localSpinnerService
        .withLocalSpinner(
          from(this.uploadFile(this.file.file))
          ,
          this.UploadingSpinnerId
        ), (url) => {
          if (!url) { return; }
          this.file.url = url;
          this.fileChange.emit(this.file);
        }
      );
    } else {
      this.fileChange.emit(this.file);
    }
  }

  private async uploadFile(file): Promise<string> {
    if (file) {
      const path = this.path ? `${this.path}/${this.prefixFileName ? this.prefixFileName + '_' : ''}${Date.now()}_${file.name}` : undefined;
      return this.firebaseService.uploadFile(file, path);
    }
    return null;
  }

  private async _initFile(url: string, prefix: string) {
    try {
      let filename = url;

      const firstPart = url.split('?alt=media');
      if (firstPart.length > 1) {
        filename = firstPart[0];
      }
      const lastPart = url.split(prefix);
      if (lastPart.length > 1) {
        filename = decodeURIComponent(lastPart[1].slice(14));
      }

      this.file = {url, file: {name: filename} as File, src: url};
    } catch (e) {
      console.log(e);
    }
  }
}
